import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../i18n';
import { token } from '../../lib/tokens';
import { pseudoPeaks, type EditorCut } from './model';

interface WaveformEditorProps {
  durationMs: number;
  peaks?: number[];
  seed: string;
  cuts: EditorCut[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onNudge: (id: string, deltaMs: number) => void;
  onConfirm: (id: string) => void;
  onDelete: (id: string) => void;
  onAdd: (atMs: number) => void;
  announce: (msg: string) => void;
}

const MARKER_LINE: Record<EditorCut['status'], string> = {
  // color + line-style + handle shape — never color alone (§5.5)
  detected: 'border-marker-detected border-dashed',
  adjusted: 'border-marker-adjusted border-solid',
  confirmed: 'border-marker-confirmed border-solid',
};

const HANDLE: Record<EditorCut['status'], string> = {
  detected: 'border border-marker-detected bg-transparent',
  adjusted: 'border border-marker-adjusted bg-marker-adjusted',
  confirmed: 'border border-marker-confirmed bg-marker-confirmed',
};

export function WaveformEditor(props: WaveformEditorProps) {
  const { durationMs, seed, cuts, selectedId, onSelect, onNudge, onConfirm, onDelete, onAdd, announce } = props;
  const { t, fmt } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const [playheadMs, setPlayheadMs] = useState(0);
  const rafRef = useRef<number | null>(null);

  const peaks = useMemo(() => props.peaks ?? pseudoPeaks(seed, 320), [props.peaks, seed]);

  // Track container width for a crisp canvas render.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Draw the waveform: rest bars, hot bars up to the playhead.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const height = canvas.clientHeight;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const rest = token('--wave-rest');
    const hot = token('--wave-hot');
    const n = peaks.length;
    const barW = width / n;
    const gap = Math.min(1.5, barW * 0.35);
    const mid = height / 2;
    const playRatio = durationMs > 0 ? playheadMs / durationMs : 0;

    for (let i = 0; i < n; i++) {
      const p = peaks[i] ?? 0;
      const barH = Math.max(2, p * (height - 6));
      const x = i * barW;
      ctx.fillStyle = i / n <= playRatio ? hot : rest;
      ctx.fillRect(x + gap / 2, mid - barH / 2, Math.max(1, barW - gap), barH);
    }
  }, [peaks, width, durationMs, playheadMs]);

  const stopPlayback = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  useEffect(() => stopPlayback, [stopPlayback]);

  /**
   * "Play segment" (§5.5): visually sweep the playhead from the selected cut to the next.
   * v0 renders the sweep without decoding audio (no autoplay, §6). A real preview player
   * lands when the presigned preview stream is wired.
   */
  const playSegment = useCallback(
    (fromMs: number, toMs: number) => {
      stopPlayback();
      const start = performance.now();
      const span = Math.max(200, toMs - fromMs);
      const tick = (now: number) => {
        const elapsed = now - start;
        const pos = Math.min(toMs, fromMs + elapsed);
        setPlayheadMs(pos);
        if (pos < toMs) rafRef.current = requestAnimationFrame(tick);
        else rafRef.current = null;
      };
      setPlayheadMs(fromMs);
      rafRef.current = requestAnimationFrame(tick);
      return span;
    },
    [stopPlayback],
  );

  const sortedIds = useMemo(() => [...cuts].sort((a, b) => a.timestampMs - b.timestampMs).map((c) => c.id), [cuts]);

  const segmentBounds = useCallback(
    (cut: EditorCut): [number, number] => {
      const next = cuts
        .filter((c) => c.timestampMs > cut.timestampMs)
        .sort((a, b) => a.timestampMs - b.timestampMs)[0];
      return [cut.timestampMs, next ? next.timestampMs : durationMs];
    },
    [cuts, durationMs],
  );

  const handleMarkerKey = useCallback(
    (e: React.KeyboardEvent, cut: EditorCut) => {
      const idx = sortedIds.indexOf(cut.id) + 1;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowRight': {
          e.preventDefault();
          const step = e.shiftKey ? 1 : e.ctrlKey || e.metaKey ? 100 : 10;
          const delta = (e.key === 'ArrowLeft' ? -1 : 1) * step;
          onNudge(cut.id, delta);
          announce(t.review.announceAdjusted(idx, fmt.durationMs(Math.max(0, cut.timestampMs + delta))));
          break;
        }
        case 'Enter':
          e.preventDefault();
          onConfirm(cut.id);
          announce(t.review.announceConfirmed(idx));
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          onDelete(cut.id);
          break;
        case ' ':
          e.preventDefault();
          playSegment(...segmentBounds(cut));
          break;
        case 'Escape':
          e.preventDefault();
          onSelect(null);
          (e.target as HTMLElement).blur();
          break;
        default:
          break;
      }
    },
    [announce, fmt, onConfirm, onDelete, onNudge, onSelect, playSegment, segmentBounds, sortedIds, t],
  );

  const onContainerKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        onAdd(playheadMs);
      }
    },
    [onAdd, playheadMs],
  );

  const onScrub = useCallback(
    (e: React.MouseEvent) => {
      const el = containerRef.current;
      if (!el || durationMs === 0) return;
      const rect = el.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      stopPlayback();
      setPlayheadMs(ratio * durationMs);
    },
    [durationMs, stopPlayback],
  );

  const ticks = useMemo(() => {
    const out: number[] = [];
    const stepMs = durationMs > 60000 ? 15000 : durationMs > 20000 ? 5000 : 2000;
    for (let ms = 0; ms <= durationMs; ms += stepMs) out.push(ms);
    return out;
  }, [durationMs]);

  return (
    <div className="select-none" onKeyDown={onContainerKey}>
      <div
        ref={containerRef}
        className="relative w-full bg-bg"
        style={{ height: 'clamp(160px, 34vh, 240px)' }}
        role="group"
        aria-label={t.review.title}
        onClick={onScrub}
      >
        <canvas ref={canvasRef} className="h-full w-full" style={{ width, height: '100%' }} />

        {/* Playhead */}
        <div
          className="pointer-events-none absolute top-0 h-full w-0.5 bg-wave-playhead"
          style={{ left: `${durationMs ? (playheadMs / durationMs) * 100 : 0}%` }}
          aria-hidden
        >
          <span className="absolute -left-1.5 top-0 block h-3 w-3 rounded-[50%] bg-wave-playhead" />
        </div>

        {/* Cut markers */}
        {cuts.map((cut) => {
          const idx = sortedIds.indexOf(cut.id) + 1;
          const left = durationMs ? (cut.timestampMs / durationMs) * 100 : 0;
          const isSelected = cut.id === selectedId;
          const stateWord = t.review.marker[cut.status];
          return (
            <div
              key={cut.id}
              role="slider"
              tabIndex={0}
              aria-label={t.review.ariaMarker(idx, fmt.durationMs(cut.timestampMs), stateWord)}
              aria-valuemin={0}
              aria-valuemax={durationMs}
              aria-valuenow={Math.round(cut.timestampMs)}
              aria-valuetext={t.review.ariaMarker(idx, fmt.durationMs(cut.timestampMs), stateWord)}
              className="focus-ring absolute top-0 z-10 flex h-full w-11 -translate-x-1/2 cursor-ew-resize justify-center rounded-sm"
              style={{ left: `${left}%`, boxShadow: isSelected ? '0 0 12px var(--accent-glow)' : undefined }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(cut.id);
              }}
              onFocus={() => onSelect(cut.id)}
              onKeyDown={(e) => handleMarkerKey(e, cut)}
            >
              {/* handle */}
              <span
                className={`absolute top-0 flex h-3 w-3 items-center justify-center ${HANDLE[cut.status]}`}
                aria-hidden
              >
                {cut.status === 'confirmed' ? (
                  <svg width={8} height={8} viewBox="0 0 8 8" fill="none" stroke="var(--ink)" strokeWidth={1.5}>
                    <path d="M1 4.2 3 6.2 7 1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
              </span>
              {/* line */}
              <span className={`h-full w-0 border-l-2 ${MARKER_LINE[cut.status]}`} aria-hidden />
            </div>
          );
        })}
      </div>

      {/* Time ruler */}
      <div className="relative mt-1 h-4 w-full" aria-hidden>
        {ticks.map((ms) => (
          <span
            key={ms}
            className="type-caption text-muted absolute -translate-x-1/2"
            style={{ left: `${durationMs ? (ms / durationMs) * 100 : 0}%` }}
          >
            {fmt.duration(ms)}
          </span>
        ))}
      </div>
    </div>
  );
}
